import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Botao,
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Indicador,
  TituloDeSecao,
} from '../../componentes/base';
import { mesPorExtenso, moeda, plural } from '../../util/formato';
import { Seletor } from '../../componentes/campos';
import { ModalNovoCliente } from '../componentes/NovoCliente';
import { useInicioDoPortal, type FaseDoPortal } from '../dados';
import { useSessaoPortal } from '../SessaoPortal';
import { Contratos } from '../componentes/ContratoDaClinica';

/**
 * Tela inicial do portal.
 *
 * A pergunta que a clínica faz ao abrir não é "quantos casos eu tenho" — é "tem
 * alguma coisa esperando POR MIM?". Por isso as pendências vêm antes do funil, e
 * cada uma é um link que já chega filtrado na lista: contar sem levar até lá
 * transformaria o cartão num aviso que ninguém sabe como resolver.
 */
export function PaginaInicioDoPortal() {
  const { token = '' } = useParams();
  const { titular, clinicas, verFinanceiro } = useSessaoPortal();
  /* A clínica escolhe o mês do RESULTADO. Fica em estado, e não na URL, porque
     é escolha de leitura momentânea — nada aqui vale um endereço guardado. */
  const [mes, setMes] = useState<string | undefined>(undefined);
  const { data, isLoading } = useInicioDoPortal(mes);
  const [cadastrando, setCadastrando] = useState(false);

  const base = `/portal/${token}`;

  return (
    <>
      <CabecalhoDaPagina
        titulo={titular ?? 'Sua carteira'}
        descricao={
          clinicas.length > 1
            ? `Acompanhamento das carteiras de ${clinicas.map((c) => c.nome).join(' e ')}`
            : 'Acompanhamento da cobrança conduzida pelo escritório'
        }
        acoes={
          <Botao tipo="primario" onClick={() => setCadastrando(true)}>
            Cadastrar cliente
          </Botao>
        }
      />

      {isLoading || !data ? (
        <Carregando />
      ) : (
        <div className="space-y-6">
          {/* O CONTRATO, antes dos números. A pergunta "até quando isso vale" vem
              antes de "quanto entrou" para quem abre o portal pensando em renovar.
              Só a data: prorrogar é conversa com o escritório, e é lá que se
              confirma — o portal não decide vigência. */}
          <Contratos contratos={data.contratos} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Indicador
              rotulo="Clientes em cobrança"
              valor={data.carteira.devedores.toLocaleString('pt-BR')}
              nota="Casos ativos na sua carteira"
            />
            <Indicador
              rotulo="Valor em cobrança"
              valor={moeda(data.carteira.valorTotal)}
              nota="Soma das dívidas na origem"
            />
          </div>

          {verFinanceiro && data.financeiro && (
            <Cartao>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <TituloDeSecao auxiliar="pagamentos dos seus clientes registrados no período">
                  Resultado do mês
                </TituloDeSecao>

                {/* `Seletor` sem `vazio` não gera opção em branco — o que se quer
                    aqui, já que sempre há um mês corrente na lista. */}
                <Seletor
                  valor={data.financeiro.noMes.mes}
                  aoMudar={setMes}
                  opcoes={data.financeiro.mesesDisponiveis.map((m) => ({
                    valor: m,
                    rotulo: mesPorExtenso(m),
                  }))}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Total que não se abre é afirmação que ninguém confere. O
                    `Indicador` já é um cartão inteiro, então o link só o envolve. */}
                <Link
                  to={`${base}/recebimentos?mes=${data.financeiro.noMes.mes}`}
                  className="block"
                >
                  <Indicador
                    rotulo="Recuperado no mês"
                    valor={moeda(data.financeiro.noMes.recebido)}
                    tom="recebido"
                    nota={
                      data.financeiro.noMes.pagamentos > 0
                        ? `${plural(data.financeiro.noMes.pagamentos, 'pagamento', 'pagamentos')} · ver de quem`
                        : 'Nenhum pagamento registrado neste mês'
                    }
                  />
                </Link>
                <Indicador
                  rotulo="Honorários do escritório no mês"
                  valor={moeda(data.financeiro.noMes.honorariosExito)}
                  nota="Honorários de êxito sobre o valor recuperado"
                />
                <Indicador
                  rotulo="Recebido no total"
                  valor={moeda(data.financeiro.totalRecebido)}
                  nota={data.financeiro.legendaDoTotal}
                />
              </div>
            </Cartao>
          )}

          <Cartao>
            <TituloDeSecao auxiliar="o escritório depende da clínica para destravar">
              Esperando por você
            </TituloDeSecao>

            <div className="grid grid-cols-1 gap-3">
              <Pendencia
                para={`${base}/clientes?pendencia=telefone`}
                quantidade={data.pendencias.semTelefone}
                titulo="sem telefone"
                explicacao="Sem telefone, a cobrança não alcança o cliente."
              />
            </div>
          </Cartao>

          <Cartao>
            {/* O rótulo diz "já em andamento" porque a soma das barras NÃO fecha com
                os clientes em cobrança lá de cima: os que ainda aguardam o primeiro
                contato ficam de fora. Sem esta ressalva, quem somar as barras conclui
                que faltam clientes. */}
            <TituloDeSecao auxiliar="clique em uma fase para ver os casos">
              Em que pé está a cobrança já em andamento
            </TituloDeSecao>
            <Funil fases={data.funil} base={base} />
          </Cartao>
        </div>
      )}

      <ModalNovoCliente aberto={cadastrando} aoFechar={() => setCadastrando(false)} />
    </>
  );
}

/**
 * Cartão de pendência.
 *
 * Zero é notícia boa e recebe tratamento de notícia boa: fica cinza e sem link, em
 * vez de virar um botão que leva a uma lista vazia.
 */
function Pendencia({
  para,
  quantidade,
  titulo,
  explicacao,
}: {
  para: string;
  quantidade: number;
  titulo: string;
  explicacao: string;
}) {
  const corpo = (
    <>
      <p
        className="tabular text-2xl leading-none font-semibold"
        style={{ color: quantidade > 0 ? 'var(--color-atencao)' : 'var(--texto-tenue)' }}
      >
        {quantidade.toLocaleString('pt-BR')}
      </p>
      <p className="mt-1.5 text-sm text-[color:var(--texto)]">
        {quantidade === 1 ? 'cliente' : 'clientes'} {titulo}
      </p>
      <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">
        {quantidade > 0 ? explicacao : 'Nada pendente aqui.'}
      </p>
    </>
  );

  if (quantidade === 0) {
    return <div className="rounded-md border p-4">{corpo}</div>;
  }

  return (
    <Link
      to={para}
      className="rounded-md border p-4 transition-colors hover:border-[color:var(--acento)]"
    >
      {corpo}
    </Link>
  );
}

/**
 * Funil da carteira.
 *
 * A largura da barra é proporcional à quantidade — o acúmulo aparece antes de
 * qualquer número ser lido, que é o que um funil existe para mostrar. Fases sem
 * nenhum caso somem: onze linhas, oito delas zeradas, escondem as três que importam.
 */
function Funil({ fases, base }: { fases: readonly FaseDoPortal[]; base: string }) {
  const comCasos = fases.filter((f) => f.quantidade > 0);
  const total = comCasos.reduce((soma, f) => soma + f.quantidade, 0);

  if (total === 0) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--texto-tenue)]">
        Nenhum cliente cadastrado ainda. Use “Cadastrar cliente” para entregar o
        primeiro caso ao escritório.
      </p>
    );
  }

  const maior = Math.max(...comCasos.map((f) => f.quantidade));

  return (
    <div className="flex flex-col gap-1">
      {comCasos.map((fase) => (
        <Link
          key={fase.fase}
          to={`${base}/clientes?fase=${fase.fase}`}
          className="grid grid-cols-[minmax(0,13rem)_1fr] items-center gap-4 rounded px-2 py-1.5 transition-colors hover:bg-[color:var(--superficie-afundada)]"
        >
          <span className="flex items-center gap-2 truncate text-sm text-[color:var(--texto)]">
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{ background: fase.cor }}
            />
            <span className="truncate">{fase.rotulo}</span>
          </span>

          <span className="relative h-6 overflow-hidden rounded-sm bg-[color:var(--superficie-afundada)]">
            <span
              className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-500"
              style={{
                width: `${(fase.quantidade / maior) * 100}%`,
                background: `color-mix(in srgb, ${fase.cor} 22%, transparent)`,
                borderLeft: `2px solid ${fase.cor}`,
              }}
            />
            <span className="tabular relative z-10 flex h-full items-center px-2.5 text-xs font-medium text-[color:var(--texto-suave)]">
              {plural(fase.quantidade, 'cliente', 'clientes')}
            </span>
          </span>
        </Link>
      ))}
    </div>
  );
}
